import {
    ActiviteDtoInput,
    ArrayOfComptaPjComptablesModelInput,
    ArticleDtoInput,
    BenefDtoInput,
    CaisseDtoInput,
    CleRepartitionModelDtoInput,
    CompteBancaireDtoInput,
    CompteDtoInput,
    CompteEcritureDtoInput,
    DevisCommandeDtoInput,
    EcheanceModelDtoInput,
    EcheancierModelDtoInput,
    ExerciceDtoInput,
    FactureDtoInput,
    FactureImputationAcompteAvoirDtoInput,
    FournisseurDtoInput,
    JournalDtoInput,
    JournalTypeDtoInput,
    ListeFacturesEtListeReglementsModelDtoInput,
    MoyenPaiementDtoInput,
    PointVenteDtoInput,
    ProduitDtoInput,
    ReglementFactureModelDtoInput,
    ReglementModelDtoInput,
    ReglementsReglementModelInput,
    RibDtoInput,
    SectionAnalytiqueModelDtoInput,
    TypeActiviteDtoInput,
    TypeMoyenPaiementDtoInput,
    UsersDtoInput
} from 'src/graphql/generated-types';
import { getXmlBoolean, getXmlLine, wrapByXmlTag } from 'src/app/utils/soap.utils';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { accountingMovementDirection } from 'src/app/board/activity/activity.constants';

export enum AccountingWritingClassType {
    extraordinaryProduct = 'EnregistrementProduitExceptionnelModel',
    refund = 'EcritureGenerationEcritureReglementModel'
}

export function createEcritureAndMouvementsAndSaveReglement(payment: ListeFacturesEtListeReglementsModelDtoInput, namespace = 'd4p1'): string {
    return `<reglementFacture xmlns:${namespace}="http://schemas.datacontract.org/2004/07/V2DTO" xmlns:i="http://www.w3.org/2001/XMLSchema-instance" >
                ${getXmlBeneficiary(payment, namespace)}
                ${getXmlCashDesk(payment.Caisse, namespace)}
                ${getXmlSupplier(payment, namespace)}
                ${getXmlInvoiceList(payment.ListeFacture?.ReglementFactureModelDTO, namespace)}
                ${getXmlSettlementModelList(payment.ListeReglementModel?.ReglementModelDTO, namespace)}
                ${getXmlSettlementList(payment.ListeReglements?.ReglementsReglementModel, namespace)}
                ${getXmlLine(payment, 'TypeEcriture')}
                ${getXmlUser(payment, namespace)}
            </reglementFacture>`;
}

function getXmlActivity(activity: ActiviteDtoInput): string {
    const getXmlActivityType = (parentObject: Record<string, any>): string => {
        const orderedProperties: (keyof TypeActiviteDtoInput)[] = ['Code', 'Id'];
        return getXmlObjectPropertyList<TypeActiviteDtoInput>(parentObject, 'TypeActivite', orderedProperties);
    };

    return !activity
        ? ''
        : `<d4p1:Activite>
                ${getXmlLine(activity, 'Id')}
                ${getXmlActivityType(activity)}
            </d4p1:Activite>`;
}

function getXmlQuotation(quotation: DevisCommandeDtoInput): string {
    return `<d4p1:DevisCommandeDTO>
                ${getXmlLine(quotation, 'ARegler')}
                ${getXmlLine(quotation, 'DateDevis')}
                ${getXmlLine(quotation, 'Id')}
                ${getXmlBoolean(quotation, 'IsCommandeEnLigne')}
                ${getXmlBoolean(quotation, 'IsCommandeEventFamiliaux')}
                ${getXmlBoolean(quotation, 'IsCommandeFinie')}
                ${getXmlBoolean(quotation, 'IsCommandeLibre')}
                ${getXmlLine(quotation, 'IsCustomLibelle', {defaultValue: 0})}
                ${getXmlBoolean(quotation, 'IsSaisieEnCours')}
                ${getXmlLine(quotation, 'MontantDossierAvecSubvention')}
                ${getXmlLine(quotation, 'MontantPresentePrestationTotal')}
                ${getXmlLine(quotation, 'MontantRemboursePrestation')}
                ${getXmlLine(quotation, 'MontantSubventionTotal')}
                ${getXmlLine(quotation, 'MontantTotalDevisAvecSubvention')}
                ${getXmlLine(quotation, 'MontantTotalDevisHorsSubvention')}
                ${getXmlBoolean(quotation, 'NecessiteAcceptationBenef')}
                ${getXmlBoolean(quotation, 'NecessiteAcceptationCE')}
                ${getXmlLine(quotation, 'Objet')}
                ${getXmlLine(quotation, 'Type')}
            </d4p1:DevisCommandeDTO>`;
}


function getXmlAvoir(avoir: FactureImputationAcompteAvoirDtoInput): string {
    return !avoir
        ? ''
        : `<d4p1:Avoir>
              ${getXmlLine(avoir, 'Id')}
          </d4p1:Avoir>`;
}

function getXmlListeFactureImpute(invoices: FactureImputationAcompteAvoirDtoInput[]): string {
    return arrayEmpty(invoices) || !invoices.some(i => !!i.Avoir)
        ? ''
        : `<d4p1:ListeFactureImpute>
                ${invoices.map(getXmlAvoir).join('')}
            </d4p1:ListeFactureImpute>`;
}

function getXmlOrder(orders: DevisCommandeDtoInput[]): string {
    return getXmlList<DevisCommandeDtoInput>(orders, 'ListeCommande', getXmlQuotation);
}

function getXmlInvoiceList(invoiceList: ReglementFactureModelDtoInput[], namespace: string): string {
    return getXmlList<ReglementFactureModelDtoInput>(invoiceList, 'ListeFacture', getXmlPaymentInvoiceModel, namespace);
}

function getXmlScheduleList(scheduleList: EcheanceModelDtoInput[], namespace: string): string {
    return getXmlList<ReglementFactureModelDtoInput>(scheduleList, 'ListeEcheances', getXmlScheduleModel, namespace);
}

function getXmlList<T>(list: T[], tagName: string, call: (object: any) => string, namespace = 'd4p1'): string {
    return arrayEmpty(list)
        ? ''
        : `<${namespace}:${tagName}>
              ${list.map(call).join('')}
           </${namespace}:${tagName}>`;
}

function getXmlInvoice(invoice: FactureDtoInput): string {
    return !invoice
        ? ''
        : `<d4p1:Facture>
                ${getXmlActivity(invoice.Activite)}
                ${getXmlBeneficiary(invoice)}
                ${getXmlObjectPropertyList<DevisCommandeDtoInput>(invoice, 'CommandeAccompte', ['Id'])}
                ${getXmlObjectPropertyList<CompteEcritureDtoInput>(invoice, 'CompteEcriture', ['Id'])}
                ${getXmlLine(invoice, 'DateFacture')}
                ${getXmlObjectPropertyList<FactureDtoInput>(invoice, 'FactureConcerneParAvoir', ['Id'])}
                ${getXmlSupplier(invoice)}
                ${getXmlLine(invoice, 'FraisCommissionFour')}
                ${getXmlLine(invoice, 'Id')}
                ${getXmlBoolean(invoice, 'IsAccompte')}
                ${getXmlBoolean(invoice, 'IsAvoir')}
                ${getXmlBoolean(invoice, 'IsLibre')}
                ${getXmlBoolean(invoice, 'IsSupprimer')}
                ${getXmlOrder(invoice.ListeCommande?.DevisCommandeDTO)}
                ${getXmlListeFactureImpute(invoice.ListeFactureImpute?.FactureImputationAcompteAvoirDTO)}
                ${getXmlLine(invoice, 'MontantFactureTotal')}
                ${getXmlLine(invoice, 'MontantFraisCB', {defaultValue: 0})}
                ${getXmlLine(invoice, 'MontantFraisPort', {defaultValue: 0})}
                ${getXmlLine(invoice, 'MontantSubventionAccordeeTotal')}
                ${getXmlLine(invoice, 'NumFacture')}
                ${getXmlLine(invoice, 'Objet')}
                ${getXmlObjectPropertyList<PointVenteDtoInput>(invoice, 'PointVente', ['ID'])}
                ${getXmlObjectPropertyList<ProduitDtoInput>(invoice, 'Produit', ['Id'])}
                ${getXmlLine(invoice, 'RemarquePrivee')}
                ${getXmlLine(invoice, 'RemarquePublique')}
                ${getXmlLine(invoice, 'RemiseGlobale')}
                ${getXmlLine(invoice, 'Solde')}
                ${getXmlLine(invoice, 'TypeFacture')}
            </d4p1:Facture>`;
}

function getXmlPaymentInvoiceModel(payment: ReglementFactureModelDtoInput): string {
    return ` <d4p1:ReglementFactureModelDTO>
                ${getXmlLine(payment, 'Status')}
                ${getXmlActivity(payment.Activite)}
                ${getXmlInvoice(payment.Facture)}
                ${getXmlBoolean(payment, 'IsEnableMonPaiement')}
                ${getXmlBoolean(payment, 'IsSelected')}
                ${getXmlLine(payment, 'LibelleEtInfoFacture')}
                ${getXmlLine(payment, 'PaiementClient')}
            </d4p1:ReglementFactureModelDTO>`;
}

function getXmlJournal(journal: JournalDtoInput): string {
    return `<d4p1:Journal>
                ${getXmlLine(journal, 'Code')}
                ${getXmlLine(journal, 'Id')}
                ${getXmlLine(journal, 'Libelle')}
                ${getXmlLine(journal, 'TypeBudget')}
                ${getXmlJournalType(journal)}
            </d4p1:Journal>`;
}

function getXmlPaymentMethod(paymentMethod: MoyenPaiementDtoInput, namespace: string): string {
    return !paymentMethod
        ? ''
        : `<${namespace}:MoyenPaiement>
                ${getXmlArticlePaymentMethod(paymentMethod)}
                ${getXmlLine(paymentMethod, 'Code')}
                ${getXmlCompte(paymentMethod, namespace)}
                ${getXmlLine(paymentMethod, 'Id')}
                ${getXmlBoolean(paymentMethod, 'IsBloqueArreteCaisse')}
                ${getXmlBoolean(paymentMethod, 'IsBloqueRemiseEnCaisse')}
                ${getXmlBoolean(paymentMethod, 'IsCloture')}
                ${getXmlBoolean(paymentMethod, 'IsCreerManuellement')}
                ${getXmlBoolean(paymentMethod, 'IsFiduciaire')}
                ${getXmlBoolean(paymentMethod, 'IsModifiable')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableCloture')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableFiduciaire')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableNecessiteCaisse')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableUseCommission')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableUtilisableAchat')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableUtilisableDirecto')}
                ${getXmlBoolean(paymentMethod, 'IsModifiableUtilisableVente')}
                ${getXmlBoolean(paymentMethod, 'IsUseCommission')}
                ${getXmlBoolean(paymentMethod, 'IsUtilisableAchat')}
                ${getXmlBoolean(paymentMethod, 'IsUtilisableDirecto')}
                ${getXmlBoolean(paymentMethod, 'IsUtilisableVente')}
                ${getXmlLine(paymentMethod, 'Libelle')}
                ${getXmlBoolean(paymentMethod, 'NecessiteCaissePourAchats')}
                ${getXmlLine(paymentMethod, 'OrdreAffichageDirecto')}
                ${getXmlLine(paymentMethod, 'OrdreAffichageGestion')}
                ${getXmlLine(paymentMethod, 'PourcentageCommissionnement')}
                ${getXmlPaymentMethodType(paymentMethod)}
            </${namespace}:MoyenPaiement>`;
}

function getXmlScheduleModel(scheduleModel: EcheanceModelDtoInput): string {
    return `<d4p1:EcheanceModelDTO>
                ${getXmlLine(scheduleModel, 'CodeTypeMoyenPaiement')}
                ${getXmlLine(scheduleModel, 'DateEcheance')}
                ${getXmlLine(scheduleModel, 'DateEcheanceMouvement')}
                ${getXmlLine(scheduleModel, 'DateRemisEnBnq')}
                ${getXmlLine(scheduleModel, 'Id')}
                ${getXmlLine(scheduleModel, 'IdCaisse')}
                ${getXmlLine(scheduleModel, 'IdCompteCollectif')}
                ${getXmlLine(scheduleModel, 'IdExercice')}
                ${getXmlLine(scheduleModel, 'IdJournal')}
                ${getXmlLine(scheduleModel, 'IsEnabled')}
                ${getXmlLine(scheduleModel, 'IsRemisEnBanque')}
                ${getXmlLine(scheduleModel, 'IsSelected')}
                ${getXmlLine(scheduleModel, 'LibelleEcheance')}
                ${getXmlLine(scheduleModel, 'MontantEcheance')}
                ${getXmlLine(scheduleModel, 'NomBanque')}
                ${getXmlLine(scheduleModel, 'NomTire')}
                ${getXmlLine(scheduleModel, 'NumeroCheque')}
            </d4p1:EcheanceModelDTO>`;
}

function getXmlSchedule(schedule: EcheancierModelDtoInput, namespace: string): string {
    return !schedule
        ? ''
        : `<${namespace}:Echeancier>
              ${getXmlLine(schedule, 'Frequence')}
              ${getXmlLine(schedule, 'Id')}
              ${getXmlLine(schedule, 'IdBenef')}
              ${getXmlLine(schedule, 'IdCompteBancaire')}
              ${getXmlLine(schedule, 'IdMoyenPaiement')}
              ${getXmlLine(schedule, 'JourEcheance')}
              ${getXmlLine(schedule, 'LibelleEcheancier')}
              ${getXmlScheduleList(schedule.ListeEcheances?.EcheanceModelDTO, namespace)}
            </${namespace}:Echeancier>`;
}

function getXmlCompteBancaire(compteBancaire: CompteBancaireDtoInput, namespace: string): string {
    return !compteBancaire
        ? ''
        : `<${namespace}:CompteBancaire>
              ${getXmlLine(compteBancaire, 'Code')}
              ${getXmlCompte(compteBancaire, namespace)}
              ${getXmlLine(compteBancaire, 'Id')}
            </${namespace}:CompteBancaire>`;

}

function getXmlSettlementModelList(settlementModel: ReglementModelDtoInput[], namespace): string {
    const getXmlSettlementModel = (settlement: ReglementModelDtoInput): string => {
        return `<${namespace}:ReglementModelDTO>
                    ${getXmlLine(settlement, 'Status')}
                    ${getXmlLine(settlement, 'Banque')}
                    ${getXmlLine(settlement, 'Commentaire')}
                    ${getXmlCompteAcompteCollectif(settlement, namespace)}
                    ${getXmlCompteBancaire(settlement.CompteBancaire, namespace)}
                    ${getXmlCompteCollectif(settlement, namespace)}
                    ${getXmlLine(settlement, 'DateEcheance')}
                    ${getXmlLine(settlement, 'DateReglement')}
                    ${getXmlExercise(settlement.Exercice, namespace)}
                    ${getXmlLine(settlement, 'Iban')}
                    ${getXmlSchedule(settlement.Echeancier, namespace)}
                    ${getXmlLine(settlement, 'IdAcompte', {defaultValue: 0})}
                    ${getXmlLine(settlement, 'IdCompteReglement')}
                    ${getXmlLine(settlement, 'IdCompteTropPercu')}
                    ${getXmlLine(settlement, 'IdDelegation')}
                    ${getXmlLine(settlement, 'IdJournal')}
                    ${getXmlLine(settlement, 'IdTransaction')}
                    ${getXmlBoolean(settlement, 'IsEnableAcompteNotBaseDonnee')}
                    ${getXmlBoolean(settlement, 'IsFromEcritureManuelle')}
                    ${getXmlBoolean(settlement, 'IsReglementEnLigne')}
                    ${getXmlBoolean(settlement, 'IsVirementCptOriginal')}
                    ${getXmlJournal(settlement.Journal)}
                    ${getXmlLine(settlement, 'Libelle')}
                    ${getXmlAttachmentsList(settlement.ListePJ, {namespace})}
                    ${getXmlLine(settlement, 'MontantReglement')}
                    ${getXmlLine(settlement, 'MontantTropPercu')}
                    ${getXmlPaymentMethod(settlement.MoyenPaiement, namespace)}
                    ${getXmlLine(settlement, 'NomTire')}
                    ${getXmlLine(settlement, 'NumCheque')}
                    ${getXmlLine(settlement, 'NumPiece')}
                    ${getXmlBoolean(settlement, 'NumPieceAuto')}
                    ${getXmlLine(settlement, 'NumnRef')}
                    ${getXmlRib(settlement, namespace)}
                    ${getXmlMandateRib(settlement, namespace)}
                </${namespace}:ReglementModelDTO>`;
    };

    return arrayEmpty(settlementModel)
        ? ''
        : `<${namespace}:ListeReglementModel>
                ${settlementModel.map(getXmlSettlementModel).join('')}
           </${namespace}:ListeReglementModel>`;
}

function getXmlAnalyticalSection(sectionAnalytique: SectionAnalytiqueModelDtoInput, namespace = 'd4p1'): string {
    return !sectionAnalytique
        ? ''
        : `<${namespace}:SectionAnalytique>
                ${getXmlLine(sectionAnalytique, 'Status')}
                ${getXmlLine(sectionAnalytique, 'Code')}
                ${getXmlLine(sectionAnalytique, 'Description')}
                ${getXmlLine(sectionAnalytique, 'Id')}
                ${getXmlLine(sectionAnalytique, 'IdPlanAnalytique')}
                ${getXmlLine(sectionAnalytique, 'IdSectionAnalytiquePere')}
                ${getXmlLine(sectionAnalytique, 'IdSectionAnalytiquePere2')}
                ${getXmlLine(sectionAnalytique, 'IdSectionAnalytiquePere3')}
                ${getXmlBoolean(sectionAnalytique, 'IsAffichCompte')}
                ${getXmlLine(sectionAnalytique, 'Libelle')}
                ${getXmlLine(sectionAnalytique, 'LibellePosteBDG')}
                ${getXmlLine(sectionAnalytique, 'LibelleSectionAnalytiquePere')}
                ${getXmlLine(sectionAnalytique, 'LibelleSectionAnalytiquePere2')}
                ${getXmlLine(sectionAnalytique, 'LibelleSectionAnalytiquePere3')}
                ${getXmlLine(sectionAnalytique, 'LibelleSectionPereFille')}
                ${getXmlLine(sectionAnalytique, 'Niveau')}
                ${getXmlLine(sectionAnalytique, 'Pourcentage')}
            </${namespace}:SectionAnalytique>`;
}

function getXmlAnalyticDistribution(anyType: CleRepartitionModelDtoInput | any): string {
    const namespace = 'd8p2';
    return `<${namespace}:anyType i:type="d4p1:CleRepartitionModelDTO">
                ${getXmlLine(anyType, 'Status', {namespace})}
                ${getXmlLine(anyType, 'IdCompte', {namespace, defaultValue: 0})}
                ${getXmlLine(anyType, 'IdMouvement', {namespace, defaultValue: 0})}
                ${getXmlLine(anyType, 'IdPlanAnalytique', {namespace})}
                ${getXmlLine(anyType, 'IdSectionAnalytique', {namespace})}
                ${getXmlBoolean(anyType, 'IsInitialSectionAnalytique', {namespace})}
                ${getXmlBoolean(anyType, 'IsSelected', {namespace})}
                ${getXmlLine(anyType, 'LibelleCompte', {namespace})}
                ${getXmlLine(anyType, 'LibellePlanAnalytique', {namespace})}
                ${getXmlLine(anyType, 'LibelleSectionAnalytique', {namespace})}
                ${getXmlLine(anyType, 'Montant', {namespace})}
                ${getXmlLine(anyType, 'MontantARepartir', {namespace})}
                ${getXmlLine(anyType, 'MontantConsultation', {namespace})}
                ${getXmlLine(anyType, 'Pourcentage', {namespace})}
                ${getXmlAnalyticalSection(anyType.SectionAnalytique)}
            </${namespace}:anyType>`;
}

function getXmlAccountingMovement(movementsList: any, anyTypeNamespace = 'd7p1'): string {
    const listeRepartitionAnalytiqueXml = (anyType: (CleRepartitionModelDtoInput | any)[], namespace = 'd8p2'): string => {
        return arrayEmpty(anyType)
            ? ''
            : `<${namespace}:ListeRepartitionAnalytique>
                    ${anyType.map(getXmlAnalyticDistribution).join('')}
                </${namespace}:ListeRepartitionAnalytique>`;
    };

    const classType = (typename: string | null): string => {
        return typename === 'EnregistrementProduitExceptionnelModelInput'
            ? 'MouvementsGenerationMouvementAnalytiqueModel'
            : 'MouvementsGenerationMouvementBase';
    };

    const anyTypeXml = (anyType: any): string => {
        const namespace = 'd8p2';
        const type = anyType.Sens === accountingMovementDirection.Debit
            ? 'MouvementsGenerationMouvementTiersModel' :
            classType(anyType?.__typename);
        return `<${anyTypeNamespace}:anyType i:type="${namespace}:${type}"
                   xmlns:${namespace}="http://schemas.datacontract.org/2004/07/V2DTO.Comptabilite.Mouvements">
                   ${getXmlLine(anyType, 'IdCompte', {namespace})}
                   ${getXmlLine(anyType, 'IdEcriture', {namespace, defaultValue: 0})}
                   ${getXmlLine(anyType, 'IdMouvement', {namespace, defaultValue: 0})}
                   ${getXmlLine(anyType, 'Montant', {namespace})}
                   ${getXmlLine(anyType, 'Sens', {namespace})}
                   ${getXmlLine(anyType, 'IdCompteTiers', {namespace})}
                   ${listeRepartitionAnalytiqueXml(anyType.ListeRepartitionAnalytique?.ArrayOfanyTypeInput)}
                </${anyTypeNamespace}:anyType>`;
    };

    return arrayEmpty(movementsList)
        ? ''
        : `<d5p1:ListeMouvements xmlns:d7p1="http://schemas.microsoft.com/2003/10/Serialization/Arrays">
                ${movementsList.map(anyTypeXml).join('')}
           </d5p1:ListeMouvements>`;
}

function getXmlAccountingWriting(write: any, parentNamespace = 'd5p1'): string {
    const classTypeName = write.__typename;
    const isRefund = classTypeName === AccountingWritingClassType.refund;
    const namespace = isRefund ? 'q3' : 'd7p2';
    const writeNamespace = {namespace: 'q3'};
    const writeTypeNamespace = {namespace: 'd8p1'};
    const attributs = isRefund
        ? ''
        : `xmlns:${namespace}="http://schemas.datacontract.org/2004/07/V2DTO.Activites.TropPercu"`;
    return !write
        ? ''
        : `<${parentNamespace}:Ecriture ${attributs}
                i:type="${namespace}:${classTypeName}"
                xmlns:${writeNamespace.namespace}="http://schemas.datacontract.org/2004/07/V2DTO.Comptabilite.Ecriture">
                ${getXmlLine(write, 'DateEcriture', writeNamespace)}
                ${getXmlLine(write, 'IdCECible', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'IdCECreePar', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'IdCESource', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'IdCaisse', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'IdEcriture', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'IdJournal', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'IdUtilisateur', {...writeNamespace, defaultValue: 0})}
                ${getXmlLine(write, 'Libelle', writeNamespace)}
                <${writeNamespace.namespace}:TypeEcriture xmlns:${writeTypeNamespace.namespace}="http://schemas.datacontract.org/2004/07/IGP.ComptabiliteEngine.EcritureComptable">
                    ${getXmlLine(write.TypeEcriture, 'Key', writeTypeNamespace)}
                </${writeNamespace.namespace}:TypeEcriture>
                ${getXmlLine(write, 'IdBeneficiaire', {namespace})}
                ${getXmlLine(write, 'IdMoyenPaiement', {namespace})}
                ${getXmlLine(write, 'Montant', {namespace})}
                ${getXmlBoolean(write, 'IsManuel', {...writeNamespace, defaultValue: false})}
            </${parentNamespace}:Ecriture>`;
}

function getXmlSettlementList(settlements: ReglementsReglementModelInput[], namespace): string {
    const settlementModelNamespace = 'd5p1';
    const getXmlSettlementModel = (settlementModel: ReglementsReglementModelInput): string => {
        return `<${settlementModelNamespace}:ReglementsReglementModel>
                   ${getXmlAccountingWriting(settlementModel.Ecriture)}
                   ${getXmlAccountingMovement(settlementModel.ListeMouvements?.anyType)}
                </${settlementModelNamespace}:ReglementsReglementModel>`;
    };

    return arrayEmpty(settlements)
        ? ''
        : `<${namespace}:ListeReglements xmlns:${settlementModelNamespace}="http://schemas.datacontract.org/2004/07/V2DTO.Reglements">
                ${settlements.map(getXmlSettlementModel).join('')}
           </${namespace}:ListeReglements>`;
}

export function getXmlAttachmentsList(attachmentsList: ArrayOfComptaPjComptablesModelInput, {namespace}: { namespace: string } = {namespace: 'd4p1'}): string {
    if (!attachmentsList || arrayEmpty(attachmentsList.ComptaPJComptablesModel)) {
        return '';
    }

    const namespacePj = {namespace: 'd6p1'};

    const xmlParameters = attachmentsList.ComptaPJComptablesModel.map(file => {
        return `<${namespacePj.namespace}:ComptaPJComptablesModel>
                    ${getXmlLine(file, 'Extension', namespacePj)}
                    ${getXmlLine(file, 'FileName', namespacePj)}
                    ${getXmlLine(file, 'FluxFichier', namespacePj)}
                    ${getXmlLine(file, 'Libelle', namespacePj)}
                </${namespacePj.namespace}:ComptaPJComptablesModel>`;
    });

    return `<${namespace}:ListePJ>
                ${xmlParameters.join('')}
            </${namespace}:ListePJ>`;
}

function getXmlRib(parentObject: Record<string, any>, namespace: string): string {
    const orderedProperties: (keyof RibDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<RibDtoInput>(parentObject, 'Rib', orderedProperties, namespace);
}

function getXmlMandateRib(parentObject: Record<string, any>, namespace: string): string {
    const orderedProperties: (keyof RibDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<RibDtoInput>(parentObject, 'RibMandat', orderedProperties, namespace);
}

function getXmlCompteAcompteCollectif(parentObject: Record<string, any>, namespace: string): string {
    const orderedProperties: (keyof CompteDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<CompteDtoInput>(parentObject, 'CompteAcompteCollectif', orderedProperties, namespace);
}

function getXmlCompte(parentObject: Record<string, any>, namespace: string): string {
    const orderedProperties: (keyof CompteDtoInput)[] = ['Code', 'Id', 'Numero'];
    return getXmlObjectPropertyList<CompteDtoInput>(parentObject, 'Compte', orderedProperties, namespace);
}

function getXmlCompteCollectif(parentObject: CompteDtoInput, namespace: string): string {
    const orderedProperties: (keyof CompteDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<CompteDtoInput>(parentObject, 'CompteCollectif', orderedProperties, namespace);
}

function getXmlSupplier(parentObject: any, namespace = 'd4p1'): string {
    const orderedProperties: (keyof FournisseurDtoInput)[] = ['Id', 'Nom'];
    return getXmlObjectPropertyList<FournisseurDtoInput>(parentObject, 'Fournisseur', orderedProperties, namespace);
}

function getXmlBeneficiary(parentObject: Record<string, any>, namespace = 'd4p1'): string {
    const orderedProperties: (keyof BenefDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<BenefDtoInput>(parentObject, 'Benef', orderedProperties, namespace);
}

function getXmlJournalType(parentObject: Record<string, any>): string {
    const orderedProperties: (keyof JournalTypeDtoInput)[] = ['Code'];
    return getXmlObjectPropertyList<JournalTypeDtoInput>(parentObject, 'TypeJournal', orderedProperties);
}

function getXmlPaymentMethodType(parentObject: Record<string, any>): string {
    const orderedProperties: (keyof TypeMoyenPaiementDtoInput)[] = ['Code', 'Id', 'Libelle'];
    return getXmlObjectPropertyList<TypeMoyenPaiementDtoInput>(parentObject, 'TypeMoyenPaiement', orderedProperties);
}

function getXmlArticlePaymentMethod(parentObject: Record<string, any>): string {
    const orderedProperties: (keyof ArticleDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<ArticleDtoInput>(parentObject, 'Article', orderedProperties);
}

function getXmlExercise(parentObject: Record<string, any>, namespace: string): string {
    const orderedProperties: (keyof ExerciceDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<ExerciceDtoInput>(parentObject, 'Exercice', orderedProperties, namespace);
}

function getXmlCashDesk(cashDesk: CaisseDtoInput, namespace: string): string {
    return !cashDesk
        ? ''
        : `<${namespace}:Caisse>
                ${getXmlLine(cashDesk, 'Code', {namespace})}
                ${getXmlLine(cashDesk, 'Id', {namespace})}
                ${getXmlBoolean(cashDesk, 'IsBloqueRemiseEnBanque', {namespace})}
                ${getXmlBoolean(cashDesk, 'IsCaisseUtilise', {namespace})}
                ${getXmlBoolean(cashDesk, 'IsCentrale', {namespace})}
                ${getXmlBoolean(cashDesk, 'IsClos', {namespace})}
                ${getXmlBoolean(cashDesk, 'IsRemiseCaisseCentrale', {namespace})}
                ${getXmlLine(cashDesk, 'Libelle', {namespace})}
            </${namespace}:Caisse>`;
}

function getXmlUser(parentObject: Record<string, any>, namespace: string): string {
    const orderedProperties: (keyof UsersDtoInput)[] = ['Id'];
    return getXmlObjectPropertyList<UsersDtoInput>(parentObject, 'User', orderedProperties);
}

function getXmlObjectPropertyList<T>(parentObject: Record<string, any>, objectName: string, orderedPropertyList: (keyof T)[], namespace = 'd4p1'): string {
    if (!parentObject || !parentObject[objectName]) {
        return '';
    }

    const stringToWrap = orderedPropertyList.map((property) => {
        return getXmlLine(parentObject[objectName], property as string, {namespace});
    });

    return wrapByXmlTag(stringToWrap.join(''), objectName, namespace);
}

